import { clone, equals, map, propOr } from "ramda";
import { createSelector } from "reselect";
import { isCompletedState } from "src/documentList/helpers";
import {
  BriefSpreadStatus,
  SPREAD_STATUSES
} from "../../../documentList/types";
import { State } from "../../../state";
import { Spread, SpreadGroundPage, SpreadSheet } from "./types";

import { Comment } from "../../../commentView/types";

export const getSpreadState = (state: State) => state.spread;

export const getSpreadLoadingStatus = createSelector(
  getSpreadState,
  spreadState => spreadState.status
);

export const getSpreadIsLoaded = createSelector(
  getSpreadLoadingStatus,
  status => equals(status, "loaded")
);

export const getSpreadHasError = createSelector(
  getSpreadState,
  state => equals(state.spread && state.spread.status, SPREAD_STATUSES.REJECTED)
);

export const getSpreadRejected = createSelector(
  getSpreadLoadingStatus,
  status => equals(status, "error")
);

export const getSpread = createSelector(
  getSpreadState,
  spreadState =>
    spreadState.status === "loaded" ? spreadState.spread : undefined
);

export const getSpreadName = createSelector<State, Spread | undefined, string>(
  getSpread,
  propOr("", "name")
);

export const getSpreadGroundPages = createSelector<
  State,
  Spread | undefined,
  SpreadGroundPage[]
>(
  getSpread,
  propOr([], "groundPages")
);

export const getSpreadPdfUrl = createSelector(
  getSpread,
  propOr("", "pdfUrl")
);

export const getConfigMetaData = createSelector(
  getSpread,
  spread => {
    if (spread && spread.financialTemplate.configMetadata) {
      return spread.financialTemplate.configMetadata;
    }
    return {};
  }
);

export const getSpreadBalanceSheets = createSelector(
  getSpread,
  spread => (spread ? spread.financialTemplate.balanceSheets : [])
);

export const getSpreadIncomeStatements = createSelector(
  getSpread,
  spread => (spread ? spread.financialTemplate.incomeStatements : [])
);

export const getSpreadCashFlows = createSelector(
  getSpread,
  spread => (spread ? spread.financialTemplate.cashFlows : [])
);

export const getSpreadSheetsPeriods = map(
  (spreadSheet: SpreadSheet) => spreadSheet.period
);

export const getSpreadIncomeStatementsPeriods = createSelector(
  getSpreadIncomeStatements,
  getSpreadSheetsPeriods
);

export const getSpreadBalanceSheetsPeriods = createSelector(
  getSpread,
  spread => (spread ? spread.financialTemplate.periods : [])
);

export const getSpreadCashFlowsPeriods = createSelector(
  getSpread,
  spread => (spread ? spread.financialTemplate.periods : [])
);

export const getDocumentExceptions = createSelector(
  getSpread,
  spread => (spread ? spread.documentExceptions : [])
);

export const getSpreadId = createSelector(
  getSpread,
  spread => (spread ? spread.id : "")
);

export const getSpreadEntityId = createSelector(
  getSpread,
  spread => (spread ? spread.entityId : "")
);

export const getSpreadCompletionStatus = createSelector(
  getSpread,
  spread => (spread ? (spread.status as BriefSpreadStatus) : undefined)
);

export const getSpreadIsCompleted = createSelector(
  getSpreadCompletionStatus,
  status => isCompletedState(status || "")
);

export const getSpreadBalanceSheetDocumentExceptions = createSelector(
  getDocumentExceptions,
  documentExceptions =>
    documentExceptions.filter(
      documentException => documentException.sheetName === "BALANCE_SHEET"
    )
);

export const getSpreadIncomeStatementDocumentExceptions = createSelector(
  getDocumentExceptions,
  documentExceptions =>
    documentExceptions.filter(
      documentException => documentException.sheetName === "INCOME_STATEMENT"
    )
);

export const getSpreadCashFlowDocumentExceptions = createSelector(
  getDocumentExceptions,
  documentExceptions =>
    documentExceptions.filter(
      documentException => documentException.sheetName === "CASH_FLOW"
    )
);

export const getSpreadIncomeStatementNode = createSelector(
  getSpreadIncomeStatements,
  incomeStatements => incomeStatements[0].node
);
export const getSpreadIncomeStatementNodeChildren = createSelector(
  getSpreadIncomeStatementNode,
  incomeStatementNode => incomeStatementNode.children
);

export const getSpreadCashFlowNode = createSelector(
  getSpreadCashFlows,
  cashFlows => cashFlows[0].node
);
export const getSpreadCashFLowNodeChildren = createSelector(
  getSpreadCashFlowNode,
  cashFlowNode => cashFlowNode.children
);

export const getSpreadBalanceSheetNode = createSelector(
  getSpreadBalanceSheets,
  balanceSheets => balanceSheets[0].node
);

export const getSpreadBalanceSheetNodeChildren = createSelector(
  getSpreadBalanceSheetNode,
  balanceSheetNode => balanceSheetNode.children
);

export const getUnmappedAccounts = createSelector(
  getSpread,
  spread => (spread && spread.unmappedAccounts ? spread.unmappedAccounts : [])
);

export const getTimstamp = createSelector(
  getSpread,
  spread => (spread ? spread.timestamp : "")
);

export const getSpreadVersion = createSelector(
  getSpreadState,
  spreadState => (spreadState.status === "loaded" ? spreadState.version : -1)
);

export const getSpreadError = createSelector(
  getSpreadState,
  spreadState =>
    spreadState.status === "error" ||
    spreadState.status === "deleted" ||
    spreadState.status === "rejected"
      ? spreadState.errorMsg
      : undefined
);

export const getSpreadStatus = createSelector(
  getSpreadState,
  spreadState => spreadState.status
);

export const getMappingNodeId = createSelector(
  getSpreadState,
  spreadState => spreadState.mappingNodeId
);

export const getAccountComments = createSelector(
  getSpreadState,
  spreadState => spreadState.accountComments
);

export const getComments = createSelector(
  getSpreadState,
  spreadState => {
    const spreadStateCopy = clone(spreadState);

    if (!spreadStateCopy.comments) {
      return [];
    }

    const transformedBody = spreadStateCopy.comments.map((comment: Comment) => {
      if (comment.rejectSpreadReason) {
        if (comment.body) {
          comment.body = comment.rejectSpreadReason + ", " + comment.body;
        } else {
          comment.body = comment.rejectSpreadReason;
        }
      }
      return comment;
    });
    return transformedBody as Comment[];
  }
);

export const getSpreadAndCommentsAreLoaded = createSelector(
  getSpreadState,
  spreadState =>
    equals(spreadState.status, "loaded") && spreadState.comments !== null
);

export const getLanguage = (state: State) =>
  state.userPreferences && state.userPreferences.language;



// WEBPACK FOOTER //
// ./src/spread/model/spread/selector.ts